import type { PopoverActions } from "@mui/material/Popover/Popover";
import { useElementResizeAndViewportOverflow } from "@sinch/hooks/utilities/useElementResizeAndViewportOverflow";
import React, { useCallback, useState } from "react";

/**
 * Open state and anchoring of the popover behind a read-only condition value input.
 * Shared by the condition inputs that edit their value in a popover instead of inline.
 */
export const useConditionValuePopover = (onBlur?: (event: any) => void) => {
  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const popoverRef = React.useRef<PopoverActions | null>(null);

  const [, setPaperRef] = useElementResizeAndViewportOverflow<HTMLDivElement>({
    callback: (overflow) => {
      if (overflow && popoverRef.current) {
        popoverRef.current.updatePosition();
      }
    },
  });

  const handleToggleOpen = useCallback<React.MouseEventHandler<HTMLDivElement>>(
    (event) => {
      setOpen((prev) => {
        if (prev) {
          onBlur?.(event);
        }
        return !prev;
      });
    },
    [onBlur]
  );

  const handleClickAway = useCallback<(event: MouseEvent | TouchEvent) => void>(
    (event) => {
      if (open) {
        setOpen(false);
        onBlur?.(event);
      }
    },
    [open, onBlur]
  );

  return {
    open,
    anchorEl,
    setAnchorEl,
    popoverRef,
    paperRef: setPaperRef,
    handleToggleOpen,
    handleClickAway,
  };
};
